import * as React from 'react';
import { useState, useEffect } from 'react';

/** Material UI Packages. */
import { Box, Typography } from '@mui/material';

/** Internal Components. */
import { customized_style } from 'shared/config/Styles';
import { MainDatagrid } from 'shared/ui/Datagrid';

/**
 * User activity panel component.
 * @param {*} param0 - Component props.
 * @param {*} param0.row - User activity row data.
 * @returns {JSX.Element} User activity panel component.
 */
const UserActivityPanel = ({ row }) => {
    /** State for checking if data is valid. */
    const [hasData, setHasData] = useState(false);

    /** Check if data is valid. */
    useEffect(() => {
        if (!row?.data || typeof row?.data !== 'object') return setHasData(false);
        setHasData(true);
    }, [row]);

    /** Transform data to table format. */
    const transformDataToTable = (data) => {
        if (!data || typeof data !== 'object') return [];

        return Object.entries(data).map(([key, value], index) => ({
            id: index,
            key: key,
            value: value,
        }));
    };

    /** Column definition. */
    const columns = [
        {
            field: 'key',
            headerName: 'Key',
            headerAlign: 'center',
            align: 'left',
            flex: 1,
            renderCell: (params) => (
                <Typography sx={customized_style.itemDatagrid_Typography}>{params.value}</Typography>
            ),
        },
        {
            field: 'value',
            headerName: 'Value',
            headerAlign: 'center',
            align: 'left',
            flex: 2,
            renderCell: (params) => {
                const formatValue = (value) => {
                    if (value === null || value === undefined) {
                        return 'N/A';
                    }

                    if (typeof value === 'object') {
                        return JSON.stringify(value, null, 2);
                    }

                    if (typeof value === 'boolean') {
                        return value ? 'Yes' : 'No';
                    }

                    if (typeof value === 'number') {
                        return value.toString();
                    }

                    return String(value);
                };

                return (
                    <Typography
                        sx={{
                            fontSize: 13,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                        }}
                    >
                        {formatValue(params.value)}
                    </Typography>
                );
            },
        },
    ];

    /** Transform data to table format. */
    const tableData = transformDataToTable(row?.data);

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '10%',
                }}
            ></Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f8f8f8',
                    px: 2,
                    py: 2,
                    height: '88%',
                }}
            >
                {hasData ? (
                    <MainDatagrid
                        rows={tableData}
                        cols={columns}
                        getRowHeight={() => 'auto'}
                        hideFooter={true}
                        sx={{
                            height: '70vh',
                            '& .MuiDataGrid-cell': {
                                borderRight: '1px solid rgba(224, 224, 224, 1)',
                            },
                            '& .MuiDataGrid-row': {
                                borderBottom: '1px solid rgba(224, 224, 224, 1)',
                            },
                        }}
                    />
                ) : (
                    <Typography sx={customized_style.itemDatagrid_Typography}>No data</Typography>
                )}
            </Box>
        </>
    );
};

export default UserActivityPanel;
